import { useState, useEffect, useCallback } from 'react';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import type { AfricanKeyboardProps, KeyboardLayer } from './keyboard.types';
import { resolveLayout } from './keyboard.layouts';
import { KEYBOARD_THEME_CSS } from './keyboard.theme';
import { KeyboardToolbar } from './KeyboardToolbar';

function nfc(s: string): string {
  return s.normalize('NFC');
}

export function AfricanKeyboard({
  language,
  value,
  onChange,
  onClose,
}: AfricanKeyboardProps) {
  const [keyboardRef, setKeyboardRef] = useState<any>(null);
  const [layer, setLayer] = useState<KeyboardLayer>('default');

  const layout = resolveLayout(language);

  useEffect(() => {
    keyboardRef?.setInput(value);
  }, [value, keyboardRef]);

  const currentRows = layout[layer] ?? layout.default;
  const layoutMap: Record<string, string[]> = { default: currentRows };
  const layoutKey = `${layer}-${currentRows.length}`;
  const isShiftActive = layer === 'uppercase' || layer === 'africanUpper';

  const toggleShift = useCallback(() => {
    setLayer((currentLayer) => {
      if (currentLayer === 'default') return 'uppercase';
      if (currentLayer === 'uppercase') return 'default';
      if (currentLayer === 'african') return 'africanUpper';
      if (currentLayer === 'africanUpper') return 'african';
      return currentLayer;
    });
  }, []);

  const handleKeyPress = useCallback((button: string) => {
    if (button === '{shift}') {
      toggleShift();
    }
  }, [toggleShift]);

  const handleChange = useCallback((newInput: string) => {
    onChange(nfc(newInput));
  }, [onChange]);

  return (
    <div
      className="relative rounded-2xl border border-border/40 bg-[#12151e]/95 backdrop-blur-2xl shadow-[0_-8px_40px_rgba(0,0,0,0.35)]"
      style={{ contain: 'layout' }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand/20 to-transparent" />

      <KeyboardToolbar
        language={language}
        layer={layer}
        onLayerChange={setLayer}
        onClose={onClose}
      />

      <div className="p-3 pt-2.5 overflow-x-auto">
        <Keyboard
          key={layoutKey}
          keyboardRef={(r: any) => setKeyboardRef(r)}
          onChange={handleChange}
          onKeyPress={handleKeyPress}
          input={value}
          display={{
            '{bksp}': '\u232B',
            '{space}': 'Espace',
            '{shift}': '⇧',
          }}
          buttonTheme={isShiftActive ? [{ class: 'yodi-shift-active', buttons: '{shift}' }] : []}
          layout={layoutMap}
          theme="hg-theme-default yodi-kb-theme"
        />
      </div>

      <style>{KEYBOARD_THEME_CSS}</style>
    </div>
  );
}
